import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CubeIcon,
  EyeIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ShoppingCartIcon,
  AdjustmentsHorizontalIcon,
  MagnifyingGlassIcon,
  DocumentArrowDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronUpDownIcon,
  PhotoIcon,
  XMarkIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline';
import { prettyCategory } from '../utils/labels';
import { Button } from '../components/ui/button';
import { ButtonGroup } from '../components/ui/button-group';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { SearchableSelect } from '../components/ui/searchable-select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import StockPdfDialog from '../components/StockPdfDialog';
import { getImageUrl } from '../utils/imagePaths';
import { getAppSocket } from '../lib/wesSocket';
import { confirmDialog } from '../components/ConfirmDialog';
import { Tip } from '../components/ui/tip';
import ProductCombobox from '../components/ProductCombobox';

interface Product {
  id: string;
  name: string;
  description?: string;
  sku: string;
  barcode?: string;
  category: string;
  subcategory?: string;
  brand?: string;
  model?: string;
  costPrice: number;
  lastCost?: number;
  sellingPrice: number;
  wholesalePrice?: number;
  currentStock: number;
  minimumStock: number;
  maximumStock?: number;
  unit: string;
  location?: string;
  shelf?: string;
  isActive: boolean;
  status?: 'active' | 'paused' | 'discontinued';
  isService: boolean;
  isBundle?: boolean;
  variantGroupId?: string;
  variantLabel?: string;
  bundleItems?: Array<{ productId: string; name: string; qty: number }>;
  requiresInstallation: boolean;
  warrantyMonths?: number;
  supplier?: {
    id: string;
    name: string;
    email: string;
    phone: string;
  };
  mainSupplierName?: string | null;
  vehicleFitment?: string | null;
  // Calculated fields
  profitMargin?: number;
  profitAmount?: number;
  isLowStock?: boolean;
  isOutOfStock?: boolean;
  imageUrl?: string | null;
}

type ProductLocation = {
  id: string;
  name: string;
};

type ProductCategoryOption = {
  id: string;
  name: string;
  description?: string | null;
  type?: string;
};

type RestockSuggestion = {
  productId: string;
  name: string;
  stock: number;
  minStock: number;
  recentUsage: number;
  recentUsageWindowDays: number;
  avgDailyUsage: number;
  targetStock: number;
  suggestedOrderQty: number;
  reason?: string;
  urgency: 'critical' | 'high' | 'medium' | 'normal';
};

type ImportProviderOption = {
  id: string;
  name: string;
  fantasyName?: string;
  esCliente?: boolean;
};

// Las mismas categorías que acepta el importador (VALID_CATEGORIES en el
// backend). Neumáticos primero porque es la lista que más se sube.
const CATEGORIAS_IMPORT = [
  { value: 'neumaticos', label: 'Neumáticos' },
  { value: 'aceites', label: 'Aceites y lubricantes' },
  { value: 'filtros', label: 'Filtros' },
  { value: 'frenos', label: 'Frenos' },
  { value: 'motor', label: 'Motor' },
  { value: 'transmision', label: 'Transmisión' },
  { value: 'electrico', label: 'Eléctrico' },
  { value: 'carroceria', label: 'Carrocería' },
  { value: 'herramientas', label: 'Herramientas' },
  { value: 'consumibles', label: 'Consumibles' },
  { value: 'otros', label: 'Otros' }
];

/** Título de columna que ordena la tabla al hacerle click. */
const ColumnaOrdenable: React.FC<{
  campo: string;
  label: string;
  sortBy: string;
  sortDir: 'asc' | 'desc';
  onSort: (campo: string) => void;
  align?: 'left' | 'center';
}> = ({ campo, label, sortBy, sortDir, onSort, align = 'left' }) => {
  const activa = sortBy === campo;
  return (
    <TableHead className={`text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap ${align === 'center' ? 'text-center' : 'text-left'}`}>
      <button
        type="button"
        onClick={() => onSort(campo)}
        className={`inline-flex items-center gap-1 transition-colors hover:text-primary ${activa ? 'text-primary' : ''}`}
      >
        {label}
        {activa
          ? (sortDir === 'asc' ? <ChevronUpIcon className="h-3.5 w-3.5" /> : <ChevronDownIcon className="h-3.5 w-3.5" />)
          : <ChevronUpDownIcon className="h-3.5 w-3.5 opacity-30" />}
      </button>
    </TableHead>
  );
};

const Products: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode } = useTheme();
  const { hasRole, hasPermission } = useAuth();
  const showFinancials = hasRole(['admin']);
  const canManageLocations = hasPermission('products');
  const canManageSettings = hasPermission('manage_settings');
  // Solo usuarios con permiso 'reports' pueden gestionar productos/servicios
  const canManageProducts = hasPermission('reports');
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [showStockPdf, setShowStockPdf] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showStockModal, setShowStockModal] = useState(false);
  const [stockAdjustType, setStockAdjustType] = useState<'add' | 'subtract' | 'set'>('add');
  const [stockAdjustQty, setStockAdjustQty] = useState('');
  const [stockAdjustReason, setStockAdjustReason] = useState('');
  const [stockAdjustSaving, setStockAdjustSaving] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadingCsv, setUploadingCsv] = useState(false);
  const [revisando, setRevisando] = useState(false);
  const [preview, setPreview] = useState<any>(null);
  // clave de la fila -> id del producto existente con el que hay que unirla
  const [decisiones, setDecisiones] = useState<Record<string, string>>({});
  const [csvPreviewRows, setCsvPreviewRows] = useState<number | null>(null);
  const [importProviders, setImportProviders] = useState<ImportProviderOption[]>([]);
  const [loadingImportProviders, setLoadingImportProviders] = useState(false);
  const [uploadConfig, setUploadConfig] = useState({
    importType: 'neumaticos' as 'neumaticos' | 'productos',
    defaultCategory: 'neumaticos',
    pricingStrategy: 'cost_plus_margin',
    utilityPercent: '35',
    duplicateMode: 'update',
    supplierName: ''
  });
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [categories, setCategories] = useState<string[]>([]);
  const [units, setUnits] = useState<string[]>([]);
  const [locations, setLocations] = useState<ProductLocation[]>([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [showLocationsModal, setShowLocationsModal] = useState(false);
  const [locationName, setLocationName] = useState('');
  const [editingLocationId, setEditingLocationId] = useState<string | null>(null);
  const [showCreateCategoryModal, setShowCreateCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDescription, setNewCategoryDescription] = useState('');
  const [savingCategory, setSavingCategory] = useState(false);
  const [catalogCategories, setCatalogCategories] = useState<ProductCategoryOption[]>([]);
  // Los filtros se guardan en sessionStorage: entrar a un producto y volver
  // (o recargar) no te hace armar el filtro de nuevo.
  const FILTROS_KEY = 'products.filtros.v1';
  const filtrosGuardados = (() => {
    try { return JSON.parse(sessionStorage.getItem(FILTROS_KEY) || 'null') || {}; } catch { return {}; }
  })();
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    brand: '',
    status: '',
    specs: '', // '' | 'con' | 'sin' especificaciones (etiquetas)
    lowStock: false,
    outOfStock: false,
    type: '', // 'product' or 'service'
    ...(filtrosGuardados.filters || {})
  } as {
    search: string; category: string; brand: string; status: string; specs: string;
    lowStock: boolean; outOfStock: boolean; type: string;
  });
  // Etiquetas elegidas para filtrar (se piden TODAS juntas, no cualquiera:
  // 'ACEA C3' + 'VW 504.00' es un aceite que cumple las dos)
  const [tagsElegidas, setTagsElegidas] = useState<string[]>(filtrosGuardados.tagsElegidas || []);
  const [tagQuery, setTagQuery] = useState('');
  const [sortBy, setSortBy] = useState(filtrosGuardados.sortBy || 'name');
  // 'asc' para el nombre (A→Z) y 'desc' para los números (de mayor a menor)
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(filtrosGuardados.sortDir || 'asc');

  useEffect(() => {
    try {
      sessionStorage.setItem(FILTROS_KEY, JSON.stringify({ filters, tagsElegidas, sortBy, sortDir }));
    } catch { /* almacenamiento lleno o bloqueado: se sigue sin persistir */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, tagsElegidas, sortBy, sortDir]);

  // Click en el título de la columna: la primera vez ordena por ella, las
  // siguientes da vuelta el orden.
  const ordenarPor = (campo: string) => {
    if (sortBy === campo) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(campo);
    setSortDir(campo === 'name' ? 'asc' : 'desc');
  };
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectAllRows, setSelectAllRows] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [restockSuggestions, setRestockSuggestions] = useState<RestockSuggestion[]>([]);
  const [showRestockSuggestions, setShowRestockSuggestions] = useState(false);
  const [showMarkupModal, setShowMarkupModal] = useState(false);
  const [applyingMarkup, setApplyingMarkup] = useState(false);
  const [markupForm, setMarkupForm] = useState({
    itemType: 'all',
    category: 'all',
    brand: '',
    model: '',
    measure: '',
    supplier: '',
    contains: '',
    markupPercent: '30',
    onlyVisible: false
  });
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const { register, handleSubmit, reset, formState: { errors }, setValue, watch } = useForm({
    defaultValues: {
      name: '',
      sku: '',
      description: '',
      category: '',
      type: '',
      brand: '',
      unit: '',
      costPrice: '',
      sellingPrice: '',
      currentStock: '',
      minimumStock: '',
      location: '',
      imageUrl: '',
      isActive: true
    }
  });

  const selectedCategory = watch('category');
  const selectedType = watch('type');
  const isService = selectedType === 'service';
  const imageUrlValue = watch('imageUrl');

  const downloadTemplate = () => {
    const isNeumaticos = uploadConfig.importType === 'neumaticos';
    const csvContent = isNeumaticos
      ? `Codigo;Modelo;Medida;Precio Lista;Costo
10020290;WANLI SP026;155/80R13 79T;93977;65126,06
10020360;WANLI SP026;165/65R13 77T;99044;68637,49
10020470;WANLI SP026;175/70R13 82T;89798;62230,01`
      : `Codigo;Nombre;Precio Lista;Costo
FLT-001;Filtro de aceite Mann W712;4500;3150
ACE-005;Aceite Mobil 1 5W30 4L;28500;19950
FRN-010;Pastillas de freno Ferodo DS2500;18900;13230`;

    const BOM = '\uFEFF';
    const csvWithBOM = BOM + csvContent;
    const filename = isNeumaticos ? 'plantilla-neumaticos.csv' : 'plantilla-productos.csv';

    const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  useEffect(() => {
    const query = new URLSearchParams(location.search).get('q');
    if (query && query !== filters.search) {
      setFilters((prev) => ({ ...prev, search: query }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleCsvFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const lowerName = file.name.toLowerCase();
    if (!lowerName.endsWith('.csv') && !lowerName.endsWith('.xlsx') && !lowerName.endsWith('.xls')) {
      toast.error('Formato no soportado. Usá CSV o Excel (.xlsx)');
      return;
    }
    setUploadFile(file);
    setCsvPreviewRows(null);
    setPreview(null);   // otro archivo, otra revision
    setDecisiones({});

    // Contar renglones solo sirve en CSV. Un .xlsx es un ZIP binario: leerlo
    // como texto y contar saltos de línea daba un número inventado (68 sobre
    // un archivo de 122 filas), que parecía que se perdía media lista.
    if (!lowerName.endsWith('.csv')) return;

    try {
      const text = await file.text();
      const lines = text
        .replace(/^\uFEFF/, '')
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter((line) => line.length > 0);
      const rows = Math.max(0, lines.length - 1);
      setCsvPreviewRows(rows);
    } catch (error) {
      console.error('Error reading CSV file:', error);
      setCsvPreviewRows(null);
    }
  };

  const fetchImportProviders = useCallback(async () => {
    try {
      setLoadingImportProviders(true);
      const response = await axios.get('/api/providers?limit=200&isActive=true');
      const providers = Array.isArray(response.data?.providers) ? response.data.providers : [];
      const options = providers
        .map((p: any) => ({
          id: String(p.id),
          name: String(p.name || '').trim(),
          fantasyName: String(p.fantasyName || '').trim(),
          esCliente: !!p.clientId
        }))
        .filter((p: ImportProviderOption) => p.name.length > 0)
        .sort((a: ImportProviderOption, b: ImportProviderOption) => a.name.localeCompare(b.name));
      setImportProviders(options);
    } catch {
      setImportProviders([]);
    } finally {
      setLoadingImportProviders(false);
    }
  }, []);

  useEffect(() => {
    if (!showUploadModal) return;
    fetchImportProviders();
  }, [showUploadModal, fetchImportProviders]);

  const renderImportProgress = (processed: number, total: number) => {
    const pct = total > 0 ? Math.round((processed / total) * 100) : 0;
    return (
      <div style={{ minWidth: 260, padding: '12px 16px', borderRadius: 8, background: 'var(--background, #1c1c1e)', border: '1px solid var(--border, #333)', boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <span style={{ fontWeight: 600, fontSize: 13 }}>Importando productos</span>
          <span style={{ fontSize: 12, color: '#999' }}>{processed}/{total}</span>
        </div>
        <div style={{ height: 6, borderRadius: 3, background: '#333', overflow: 'hidden' }}>
          <div style={{ height: '100%', borderRadius: 3, background: '#22c55e', width: `${pct}%`, transition: 'width 0.3s ease' }} />
        </div>
        <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>{pct}% completado</div>
      </div>
    );
  };

  /** Arma el FormData comun al simulacro y a la importacion real. */
  const armarFormData = (extra?: Record<string, string>) => {
    const fd = new FormData();
    fd.append('csvFile', uploadFile as File);
    fd.append('defaultCategory', uploadConfig.defaultCategory);
    fd.append('pricingStrategy', uploadConfig.pricingStrategy);
    fd.append('utilityPercent', uploadConfig.utilityPercent || '0');
    fd.append('duplicateMode', uploadConfig.duplicateMode);
    fd.append('importSupplierName', uploadConfig.supplierName || '');
    Object.entries(extra || {}).forEach(([k, v]) => fd.append(k, v));
    return fd;
  };

  /**
   * Paso previo: el backend calcula todo sin escribir y devuelve que va a
   * pasar. Sirve sobre todo para los dudosos: filas que crearian un producto
   * nuevo cuando en realidad ya existe cargado con otro nombre.
   */
  const revisarAntesDeImportar = async () => {
    if (!uploadFile) {
      toast.error('Elegí un archivo antes de importar');
      return;
    }
    setRevisando(true);
    try {
      const { data } = await axios.post('/api/products/upload-csv', armarFormData({ dryRun: 'true' }), {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setPreview(data);
      setDecisiones({});
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'No se pudo revisar el archivo');
    } finally {
      setRevisando(false);
    }
  };

  const submitCsvImport = async () => {
    if (!uploadFile) {
      toast.error('Selecciona un archivo CSV antes de importar');
      return;
    }

    const formData = new FormData();
    formData.append('csvFile', uploadFile);
    formData.append('defaultCategory', uploadConfig.defaultCategory);
    formData.append('pricingStrategy', uploadConfig.pricingStrategy);
    formData.append('utilityPercent', uploadConfig.utilityPercent || '0');
    formData.append('duplicateMode', uploadConfig.duplicateMode);
    formData.append('importSupplierName', uploadConfig.supplierName || '');
    if (Object.keys(decisiones).length) formData.append('decisiones', JSON.stringify(decisiones));

    const totalRows = csvPreviewRows || 0;

    // Cerrar modal y mostrar toast de progreso
    setShowUploadModal(false);
    setUploadFile(null);
    setCsvPreviewRows(null);
    setPreview(null);
    setDecisiones({});
    setUploadingCsv(true);

    toast.custom(() => renderImportProgress(0, totalRows), { id: 'csv-import', duration: Infinity });

    // POST fire-and-forget: el resultado se obtiene del polling
    axios.post('/api/products/upload-csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).catch(() => {}); // errores se capturan vía polling

    // Polling de progreso cada 500ms
    const pollInterval = setInterval(async () => {
      try {
        const { data } = await axios.get('/api/products/import-progress');
        if (data.status === 'running') {
          toast.custom(() => renderImportProgress(data.processed || 0, data.total || totalRows), { id: 'csv-import', duration: Infinity });
        } else if (data.status === 'completed') {
          clearInterval(pollInterval);
          setUploadingCsv(false);
          toast.dismiss('csv-import');
          const success = (data.created || 0) + (data.updated || 0);
          setTimeout(() => {
            toast.success(`Importación completada: ${success}/${data.total || totalRows} registros`);
            if (data.errors > 0) {
              toast.error(`${data.errors} registros con errores`);
            }
          }, 300);
          fetchProducts();
        } else if (data.status === 'error') {
          clearInterval(pollInterval);
          setUploadingCsv(false);
          toast.dismiss('csv-import');
          setTimeout(() => toast.error(data.error || 'Error al importar'), 300);
        }
      } catch {
        // Si falla el polling, seguir intentando
      }
    }, 500);

    // Timeout de seguridad: parar polling después de 5 minutos
    setTimeout(() => {
      clearInterval(pollInterval);
      setUploadingCsv(false);
    }, 300_000);
  };

  const fetchProducts = useCallback(async (options?: { silent?: boolean }) => {
    const silent = options?.silent === true;
    if (!silent) setLoading(true);
    try {
      // Se traen TODOS una vez y los filtros se aplican en la pantalla: antes
      // cada cambio de categoria volvia a pedir la lista y se veia el parpadeo
      // de recarga para algo que ya estaba en memoria.
      const params = new URLSearchParams();
      params.append('limit', 'all');

      const response = await axios.get(`/api/products?${params}`);
      const mapped = response.data.products.map((p: any) => ({
        ...p,
        sellingPrice: typeof p.sellingPrice !== 'undefined' ? p.sellingPrice : p.salePrice,
        currentStock: typeof p.currentStock !== 'undefined' ? p.currentStock : p.stock,
        minimumStock: typeof p.minimumStock !== 'undefined' ? p.minimumStock : p.minStock,
        status: p.status || (p.isActive ? 'active' : 'paused')
      }));

      setProducts(mapped);
    } catch (error) {
      toast.error('Error al cargar los productos');
    } finally {
      if (!silent) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRestockSuggestions = useCallback(async () => {
    try {
      const response = await axios.get('/api/products/restock-suggestions?lookbackDays=90&coverDays=21');
      setRestockSuggestions(response.data?.suggestions || []);
    } catch (error) {
      console.error('Error fetching restock suggestions:', error);
      setRestockSuggestions([]);
    }
  }, []);

  // Fetch data on mount and when filters (except search) change
  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchUnits();
    fetchRestockSuggestions();
  }, [fetchProducts, fetchRestockSuggestions]);

  // Realtime: refrescar en vivo por sucursal (sin polling)
  useEffect(() => {
    const socket = getAppSocket();
    let refreshTimer: number | null = null;

    const scheduleSilentRefresh = (payload?: any) => {
      const path = String(payload?.path || '');
      if (
        path &&
        !(
          path.startsWith('/api/products') ||
          path.startsWith('/api/purchases') ||
          path.startsWith('/api/supplies') ||
          path.startsWith('/api/remitos') ||
          path.startsWith('/api/invoices') ||
          path.startsWith('/api/settings/product-locations')
        )
      ) {
        return;
      }

      if (refreshTimer) window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(() => {
        fetchProducts({ silent: true });
        fetchRestockSuggestions();
      }, 450);
    };

    socket.on('area:update', scheduleSilentRefresh);
    socket.on('connect', scheduleSilentRefresh);

    return () => {
      if (refreshTimer) window.clearTimeout(refreshTimer);
      socket.off('area:update', scheduleSilentRefresh);
      socket.off('connect', scheduleSilentRefresh);
    };
  }, [fetchProducts, fetchRestockSuggestions]);


  useEffect(() => {
    if (showCreateModal && selectedProduct) {
      setValue('name', selectedProduct.name || '');
      setValue('sku', selectedProduct.sku || '');
      setValue('description', selectedProduct.description || '');
      setValue('category', selectedProduct.category || '');
      setValue('type', selectedProduct.isService ? 'service' : 'product');
      setValue('brand', selectedProduct.brand || '');
      setValue('unit', selectedProduct.unit || '');
      setValue('costPrice', selectedProduct.costPrice != null ? String(selectedProduct.costPrice) : '');
      setValue('sellingPrice', selectedProduct.sellingPrice != null ? String(selectedProduct.sellingPrice) : '');
      setValue('currentStock', selectedProduct.currentStock != null ? String(selectedProduct.currentStock) : '');
      setValue('minimumStock', selectedProduct.minimumStock != null ? String(selectedProduct.minimumStock) : '');
      setValue('location', selectedProduct.location || '');
      setValue('imageUrl', selectedProduct.imageUrl || '');
      setValue('isActive', selectedProduct.isActive !== false);
      setImagePreview(selectedProduct.imageUrl || null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } else if (showCreateModal && !selectedProduct) {
      reset();
      setImagePreview(null);
      setValue('imageUrl', '');
      setValue('isActive', true);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [showCreateModal, selectedProduct, setValue, reset]);

  const normalizeCategoryInput = (value: string) =>
    String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9\s_-]/g, '')
      .trim()
      .replace(/\s+/g, '_');

  const fetchCategories = async () => {
    try {
      // Fuente única: la tabla de categorías gestionada
      const categoryResponse = await axios.get('/api/categories?type=product');
      const merged = new Set<string>();
      const extraCategories = categoryResponse.data.categories || [];
      setCatalogCategories(
        extraCategories
          .map((cat: any) => ({
            id: cat?.id,
            name: normalizeCategoryInput(cat?.name || ''),
            description: cat?.description || '',
            type: cat?.type || 'product',
          }))
          .filter((cat: ProductCategoryOption) => Boolean(cat.name))
      );
      extraCategories.forEach((cat: any) => {
        const normalized = normalizeCategoryInput(cat?.name || '');
        if (normalized) merged.add(normalized);
      });

      // Asegurar que "servicio" est?? siempre incluido
      if (!merged.has('servicio')) {
        merged.add('servicio');
      }

      const sorted = Array.from(merged).sort((a, b) => a.localeCompare(b));
      setCategories(sorted);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories(['servicio', 'neumaticos', 'aceites', 'filtros', 'frenos', 'otros']);
      setCatalogCategories([]);
    }
  };

  const fetchUnits = async () => {
    try {
      const response = await axios.get('/api/products/units');
      setUnits(response.data.units);
    } catch (error) {
      console.error('Error fetching units:', error);
    }
  };

  const fetchLocations = useCallback(async () => {
    try {
      setLoadingLocations(true);
      const response = await axios.get('/api/settings/product-locations');
      setLocations(response.data?.locations || []);
    } catch (error) {
      console.error('Error fetching product locations:', error);
      setLocations([]);
    } finally {
      setLoadingLocations(false);
    }
  }, []);

  const resetLocationForm = () => {
    setLocationName('');
    setEditingLocationId(null);
  };

  const handleSaveLocation = async () => {
    const trimmed = locationName.trim();
    if (!trimmed) {
      toast.error('El nombre de la ubicacion es obligatorio');
      return;
    }

    try {
      if (editingLocationId) {
        await axios.put(`/api/settings/product-locations/${editingLocationId}`, { name: trimmed });
        toast.success('Ubicacion actualizada');
      } else {
        await axios.post('/api/settings/product-locations', { name: trimmed });
        toast.success('Ubicacion creada');
      }
      resetLocationForm();
      fetchLocations();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'No se pudo guardar la ubicacion');
    }
  };

  const handleEditLocation = (location: ProductLocation) => {
    setEditingLocationId(location.id);
    setLocationName(location.name);
  };

  const handleDeleteLocation = async (locationId: string) => {
    if (!await confirmDialog('Estas seguro de eliminar la ubicacion?')) return;
    try {
      await axios.delete(`/api/settings/product-locations/${locationId}`);
      toast.success('Ubicacion eliminada');
      fetchLocations();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'No se pudo eliminar la ubicacion');
    }
  };

  const handleCreateCategory = async () => {
    const normalizedName = normalizeCategoryInput(newCategoryName);
    if (!normalizedName) {
      toast.error('Ingresa un nombre de categoria valido');
      return;
    }

    if (categories.includes(normalizedName)) {
      toast.error('Esa categoria ya existe');
      return;
    }

    try {
      setSavingCategory(true);
      await axios.post('/api/categories', {
        name: normalizedName,
        description: newCategoryDescription.trim() || null,
        type: 'product',
      });

      setCategories((prev) => [...prev, normalizedName].sort((a, b) => a.localeCompare(b)));
      await fetchCategories();
      setNewCategoryName('');
      setNewCategoryDescription('');
      setShowCreateCategoryModal(false);
      toast.success('Categoria creada');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'No se pudo crear la categoria');
    } finally {
      setSavingCategory(false);
    }
  };

  useEffect(() => {
    if (showLocationsModal) {
      fetchLocations();
    } else {
      resetLocationForm();
    }
  }, [showLocationsModal, fetchLocations]);

  useEffect(() => {
    if (showCreateCategoryModal) {
      fetchCategories();
    }
  }, [showCreateCategoryModal]);

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Solo se permiten imagenes');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen no puede superar 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('image', file);

      // Usar directamente el endpoint de insumos (estable) para evitar timeouts del de productos
      const res = await axios.post('/api/supplies/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 20000
      });
      const uploadedUrl: string | undefined = res.data?.imageUrl;

      if (uploadedUrl) {
        setImagePreview(uploadedUrl);
        setValue('imageUrl', uploadedUrl);

        // Si se est?? editando desde este modal, persistir la imagen inmediatamente
        if (selectedProduct?.id) {
          try {
            await axios.put(`/api/products/${selectedProduct.id}`, { imageUrl: uploadedUrl });
          } catch (saveErr: any) {
            console.error('Error guardando imagen del producto:', saveErr);
            toast.error(saveErr?.response?.data?.error || 'No se pudo guardar la imagen en el producto');
          }
        }

        toast.success('Imagen subida correctamente');
      } else {
        toast.error('No se pudo obtener la URL de la imagen');
      }
    } catch (error: any) {
      console.error('Error uploading image:', error);
      toast.error(error.response?.data?.error || 'Error al subir la imagen');
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImage = () => {
    setImagePreview(null);
    setValue('imageUrl', '');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Create product
  const onSubmit = async (data: any) => {
    // Si no hay SKU, generar uno autom??tico
    if (!data.sku || data.sku.trim() === "") {
      const prefix = data.type === 'service' ? 'SRV-' : 'PRD-';
      data.sku = prefix + Date.now();
    }

    // Determinar si es servicio basado en el tipo
    const isService = data.type === 'service';
    const normalizedImageUrl = (data.imageUrl || '').toString().trim();

    // Mapear nombres de campos para el backend
    const payload: any = {
      ...data,
      salePrice: Number(data.sellingPrice),
      stock: isService ? undefined : Number(data.currentStock ?? 0),
      minStock: isService ? undefined : Number(data.minimumStock ?? 0),
      costPrice: isService ? undefined : (data.costPrice === '' || data.costPrice === null || typeof data.costPrice === 'undefined' ? undefined : Number(data.costPrice)),
      isService: isService,
      isActive: data.isActive !== false && data.isActive !== 'false',
      imageUrl: normalizedImageUrl || null
    };

    delete payload.sellingPrice;
    delete payload.currentStock;
    delete payload.minimumStock;
    delete payload.type;

    // Si es servicio, eliminar campos que no corresponden
    if (isService) {
      delete payload.costPrice;
      delete payload.stock;
      delete payload.minStock;
      delete payload.unit;
      delete payload.brand;
    }

    // Si es edici??n y el SKU no cambi??, no enviar el campo SKU
    if (selectedProduct && data.sku === selectedProduct.sku) {
      delete payload.sku;
    }

    try {
      if (selectedProduct) {
        await axios.put(`/api/products/${selectedProduct.id}`, payload);
        toast.success('Producto actualizado exitosamente');
      } else {
        await axios.post('/api/products', payload);
        toast.success('Producto creado exitosamente');
      }
      setShowCreateModal(false);
      setSelectedProduct(null);
      setImagePreview(null);
      setValue('imageUrl', '');
      reset();
      fetchProducts();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al guardar el producto');
    }
  };

  // Ajuste manual de stock
  const onStockAdjust = async () => {
    if (!selectedProduct) { toast.error('Elegí el producto'); return; }
    const qty = parseInt(stockAdjustQty, 10);
    let sendQty = qty;
    let sendType: 'add' | 'subtract' = stockAdjustType === 'subtract' ? 'subtract' : 'add';
    if (stockAdjustType === 'set') {
      // Stock real contado: se manda la diferencia contra el stock actual
      if (Number.isNaN(qty) || qty < 0) { toast.error('Ingresá el stock real (0 o más)'); return; }
      const delta = qty - Number(selectedProduct.currentStock ?? 0);
      if (delta === 0) { toast.info('El stock ya está en ese valor'); return; }
      sendQty = Math.abs(delta);
      sendType = delta > 0 ? 'add' : 'subtract';
    } else if (!qty || qty <= 0) { toast.error('Ingresá una cantidad válida (mayor a 0)'); return; }
    setStockAdjustSaving(true);
    try {
      await axios.post(`/api/products/${selectedProduct.id}/stock`, {
        quantity: sendQty,
        type: sendType,
        reason: stockAdjustReason.trim() || undefined
      });
      toast.success(stockAdjustType === 'set'
        ? `Stock de "${selectedProduct.name}" ajustado a ${qty}`
        : `${stockAdjustType === 'add' ? 'Entrada' : 'Salida'} de ${qty} unidades registrada`);
      setShowStockModal(false);
      setSelectedProduct(null);
      setStockAdjustQty('');
      setStockAdjustReason('');
      setStockAdjustType('add');
      fetchProducts();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al ajustar el stock');
    } finally {
      setStockAdjustSaving(false);
    }
  };

  // Delete product
  const handleDelete = async (productId: string) => {

    try {
      await axios.delete(`/api/products/${productId}`);
      toast.success('Producto eliminado exitosamente');
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
      fetchProducts();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al eliminar el producto');
    }
  };

  const handleDeleteSelected = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (!await confirmDialog(`Eliminar ${ids.length} item(s) seleccionado(s)?`)) return;

    setBulkDeleting(true);
    try {
      const response = await axios.delete('/api/products/bulk', { data: { ids } });
      const deleted = response.data?.deleted ?? ids.length;
      if (deleted > 0) {
        toast.success(`${deleted} item(s) eliminado(s)`);
      }

      setSelectedIds(new Set());
      setSelectAllRows(false);
      fetchProducts();
    } finally {
      setBulkDeleting(false);
    }
  };

  const handlePreviewImage = (imagePath?: string | null) => {
    if (!imagePath) return;
    const url = getImageUrl(imagePath);
    if (url) {
      setPreviewImageUrl(url);
    }
  };

  const normalizeCategoryKey = (value?: string | null): string => {
    const raw = String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();

    // Datos legacy pueden venir con mojibake; los normalizamos por tokens.
    if (raw.includes('neum')) return 'neumaticos';
    if (raw.includes('serv')) return 'servicio';
    if (raw.includes('aceit')) return 'aceites';
    if (raw.includes('filtr')) return 'filtros';
    if (raw.includes('fren')) return 'frenos';
    if (raw.includes('motor')) return 'motor';
    if (raw.includes('transm')) return 'transmision';
    if (raw.includes('elect')) return 'electrico';
    if (raw.includes('carro')) return 'carroceria';
    if (raw.includes('herram')) return 'herramientas';
    if (raw.includes('consum')) return 'consumibles';
    if (raw.includes('otro')) return 'otros';

    return raw;
  };

  const getCategoryLabel = (category: string) => prettyCategory(category);

  const getUnitLabel = (unit: string) => {
    const unitMap: { [key: string]: string } = {
      unidad: 'Unidad',
      par: 'Par',
      litro: 'Litro',
      metro: 'Metro',
      kg: 'Kilogramo',
      caja: 'Caja',
      rollo: 'Rollo'
    };
    return unitMap[unit] || unit;
  };

  const normalizeText = (value?: string | null) =>
    String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();

  const detectMeasure = (name?: string | null) => {
    const text = String(name || '');
    const match =
      text.match(/\b\d{3}\/\d{2,3}R\d{2,3}[A-Z]?\b/i) ||
      text.match(/\b\d{2,3}R\d{2,3}(?:\.\d)?\b/i);
    return match ? match[0] : '';
  };

  const matchesMarkupCriteria = useCallback((product: Product) => {
    const productCategory = normalizeCategoryKey(product.category);
    if (markupForm.itemType === 'product' && productCategory === 'servicio') return false;
    if (markupForm.itemType === 'service' && productCategory !== 'servicio') return false;
    if (markupForm.category !== 'all' && productCategory !== normalizeCategoryKey(markupForm.category)) return false;

    const normalizedName = normalizeText(product.name);
    const normalizedDescription = normalizeText(product.description);
    const normalizedBrand = normalizeText(product.brand);
    const normalizedModel = normalizeText(product.model);
    const normalizedSupplier = normalizeText(product.mainSupplierName);
    const normalizedMeasure = normalizeText(detectMeasure(product.name));

    const brandFilter = normalizeText(markupForm.brand);
    const modelFilter = normalizeText(markupForm.model);
    const measureFilter = normalizeText(markupForm.measure);
    const supplierFilter = normalizeText(markupForm.supplier);
    const containsFilter = normalizeText(markupForm.contains);

    if (brandFilter && !(normalizedBrand.includes(brandFilter) || normalizedName.includes(brandFilter))) return false;
    if (modelFilter && !(normalizedModel.includes(modelFilter) || normalizedName.includes(modelFilter))) return false;
    if (measureFilter && !(normalizedMeasure.includes(measureFilter) || normalizedName.includes(measureFilter))) return false;
    if (supplierFilter && !normalizedSupplier.includes(supplierFilter)) return false;
    if (containsFilter && !(normalizedName.includes(containsFilter) || normalizedDescription.includes(containsFilter))) return false;

    return true;
  }, [markupForm]);

  const formatMoney = (value?: number | null) => {
    const parsed = Number(value || 0);
    if (!Number.isFinite(parsed) || parsed <= 0) return '-';
    return `$${parsed.toLocaleString('es-AR')}`;
  };

  const getStockStatusColor = (product: Product) => {
    if (product.isOutOfStock) return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
    if (product.isLowStock) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
    return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
  };

  const getStockStatusText = (product: Product) => {
    if (product.isOutOfStock) return 'Critico';
    if (product.isLowStock) return 'Reponer';
    return 'Ok';
  };

  const sortedProducts = useMemo(() => {
    const list = [...products];
    const getMarginAmount = (product: Product) => {
      const cost = Number(product.costPrice || 0);
      const sale = Number(product.sellingPrice || 0);
      return sale > 0 ? (sale - cost) : 0;
    };
    const getMarginPercent = (product: Product) => {
      const cost = Number(product.costPrice || 0);
      const sale = Number(product.sellingPrice || 0);
      return cost > 0 ? ((sale - cost) / cost) * 100 : 0;
    };

    // El nombre arranca A→Z y los números de mayor a menor, que es lo que uno
    // quiere ver primero (lo más caro, lo que más stock tiene). `sortDir`
    // invierte eso al volver a tocar la columna.
    const orden = (valor: (p: Product) => number) =>
      list.sort((a, b) => (sortDir === 'asc' ? valor(a) - valor(b) : valor(b) - valor(a)));

    switch (sortBy) {
      case 'marginPercent':
        return orden(getMarginPercent);
      case 'price':
        return orden((p) => Number(p.sellingPrice || 0));
      case 'cost':
        return orden((p) => Number(p.costPrice || 0));
      case 'stock':
        return orden((p) => Number(p.currentStock || 0));
      case 'specsDate':
        // Sin fecha primero (nunca revisados), despues de la mas vieja a la mas nueva
        return orden((p) => {
          const f = (p as any).specsUpdatedAt;
          return f ? new Date(f).getTime() : 0;
        });
      default:
        return list.sort((a, b) => {
          const cmp = (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' });
          return sortDir === 'desc' ? -cmp : cmp;
        });
    }
  }, [products, sortBy, sortDir]);

  /**
   * Etiquetas disponibles para filtrar, con cuántos productos tiene cada una.
   * Salen de lo que hay a la vista: si elegiste una categoría, solo las de esa
   * categoría, así no ofrece normas de aceite mientras mirás neumáticos.
   */
  const etiquetasDisponibles = useMemo(() => {
    // Sin categoría elegida no se ofrece ninguna: mezclar normas de aceite con
    // las de otra cosa no sirve para nada, primero se acota el rubro.
    if (!filters.category) return [];
    const cuenta = new Map<string, number>();
    sortedProducts.filter((p) => String(p.category || '') === filters.category).forEach((p) => {
      ((p as any).tags || []).forEach((t: string) => {
        if (t) cuenta.set(t, (cuenta.get(t) || 0) + 1);
      });
    });
    return Array.from(cuenta.entries())
      .map(([tag, n]) => ({ tag, n }))
      .sort((a, b) => b.n - a.n || a.tag.localeCompare(b.tag, 'es'));
  }, [sortedProducts, filters.category]);

  const alternarTag = (tag: string) => {
    setTagsElegidas((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));
  };

  const marcasDisponibles = useMemo(() => {
    const vistas = new Map<string, string>();
    const base = filters.category
      ? sortedProducts.filter((p) => String(p.category || '') === filters.category)
      : sortedProducts;
    base.forEach((p) => {
      const marca = String((p as any).brand || '').trim();
      if (!marca) return;
      const clave = marca.toLowerCase();
      if (!vistas.has(clave)) vistas.set(clave, marca);
    });
    return Array.from(vistas.values()).sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
  }, [sortedProducts, filters.category]);

  const filteredProducts = useMemo(() => {
    const search = String(filters.search || '').trim().toLowerCase();

    // Todos los filtros se resuelven acá, sobre lo que ya está cargado
    let base = sortedProducts;
    if (filters.category) base = base.filter((p) => String(p.category || '') === filters.category);
    if (filters.status) base = base.filter((p) => String((p as any).status || '') === filters.status);
    if (filters.brand) base = base.filter((p) => String((p as any).brand || '').toLowerCase() === filters.brand.toLowerCase());
    if ((filters as any).specs === 'con') base = base.filter((p) => Boolean((p as any).specsUpdatedAt));
    if ((filters as any).specs === 'sin') base = base.filter((p) => !((p as any).specsUpdatedAt));
    if (filters.type === 'service') base = base.filter((p) => (p as any).isService || p.category === 'servicio');
    if (filters.type === 'product') base = base.filter((p) => !((p as any).isService || p.category === 'servicio'));
    if (filters.lowStock) base = base.filter((p) => (p as any).isLowStock);
    if (filters.outOfStock) base = base.filter((p) => (p as any).isOutOfStock);

    const conTags = tagsElegidas.length
      ? base.filter((p) => {
          const suyas = ((p as any).tags || []).map((t: string) => t.toLowerCase());
          return tagsElegidas.every((t) => suyas.includes(t.toLowerCase()));
        })
      : base;
    if (!search) return conTags;
    return conTags.filter((product) => {
      const name = String(product.name || '').toLowerCase();
      const sku = String(product.sku || '').toLowerCase();
      const category = String(product.category || '').toLowerCase();
      const description = String(product.description || '').toLowerCase();
      // Las etiquetas (normas API/ACEA/VW) tambien se buscan: es como se
      // encuentra un aceite por lo que pide el auto.
      const tags = ((product as any).tags || []).join(' ').toLowerCase();
      return (
        name.includes(search) ||
        sku.includes(search) ||
        category.includes(search) ||
        description.includes(search) ||
        tags.includes(search)
      );
    });
  }, [sortedProducts, filters, tagsElegidas]);

  const categoryOptions = useMemo(() => {
    const set = new Set<string>();
    for (const p of products) {
      const normalized = normalizeCategoryKey(p.category);
      if (normalized) set.add(normalized);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [products]);

  const selectedMarkupCategory = normalizeCategoryKey(markupForm.category);
  useEffect(() => {
    if (markupForm.itemType === 'service' && selectedMarkupCategory !== 'all' && selectedMarkupCategory !== 'servicio') {
      setMarkupForm((prev) => ({ ...prev, category: 'all' }));
    }
    if (markupForm.itemType === 'product' && selectedMarkupCategory === 'servicio') {
      setMarkupForm((prev) => ({ ...prev, category: 'all' }));
    }
  }, [markupForm.itemType, selectedMarkupCategory]);

  const markupSourceProducts = markupForm.onlyVisible ? filteredProducts : products;

  const matchedMarkupProducts = useMemo(
    () => markupSourceProducts.filter(matchesMarkupCriteria),
    [markupSourceProducts, markupForm]
  );

  const handleApplyBulkMarkup = async () => {
    const targetMarkup = Number(markupForm.markupPercent);
    if (!Number.isFinite(targetMarkup) || targetMarkup < 0) {
      toast.error('Ingresa un markup valido (0 o mayor)');
      return;
    }

    const candidates = matchedMarkupProducts.filter(
      (p) => p.category !== 'servicio' && Number(p.costPrice || 0) > 0
    );

    if (!candidates.length) {
      toast.error('No hay productos con costo valido para aplicar el markup');
      return;
    }

    setApplyingMarkup(true);
    let updated = 0;
    let failed = 0;

    try {
      for (let i = 0; i < candidates.length; i += 8) {
        const chunk = candidates.slice(i, i + 8);
        const chunkResults = await Promise.all(
          chunk.map(async (product) => {
            try {
              const cost = Number(product.costPrice || 0);
              const salePrice = Math.round(cost * (1 + targetMarkup / 100) * 100) / 100;
              await axios.put(`/api/products/${product.id}`, { salePrice });
              return true;
            } catch (error) {
              console.error('Error applying markup to product', product.id, error);
              return false;
            }
          })
        );
        updated += chunkResults.filter(Boolean).length;
        failed += chunkResults.filter((ok) => !ok).length;
      }

      await fetchProducts();
      const skipped = matchedMarkupProducts.length - candidates.length;
      toast.success(
        `Markup aplicado. Actualizados: ${updated}. Omitidos: ${skipped}. Errores: ${failed}.`
      );
      setShowMarkupModal(false);
    } finally {
      setApplyingMarkup(false);
    }
  };

  const totalResults = filteredProducts.length;
  const visibleIds = useMemo(() => filteredProducts.map((product) => product.id), [filteredProducts]);
  const selectedVisibleCount = useMemo(
    () => visibleIds.filter((id) => selectedIds.has(id)).length,
    [visibleIds, selectedIds]
  );
  const totalPages = Math.max(1, Math.ceil(totalResults / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedProducts = filteredProducts.slice(startIndex, endIndex);
  const startResult = totalResults === 0 ? 0 : startIndex + 1;
  const endResult = Math.min(endIndex, totalResults);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters.search, filters.category, filters.brand, (filters as any).specs, filters.status, filters.lowStock, filters.outOfStock, filters.type]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  useEffect(() => {
    if (!visibleIds.length) {
      setSelectAllRows(false);
      return;
    }
    setSelectAllRows(visibleIds.every((id) => selectedIds.has(id)));
  }, [visibleIds, selectedIds]);

  // Funciones de navegaci??n de paginaci??n
  const goToFirstPage = () => setCurrentPage(1);
  const goToPreviousPage = () => setCurrentPage(prev => Math.max(1, prev - 1));
  const goToNextPage = () => setCurrentPage(prev => Math.min(totalPages, prev + 1));
  const goToLastPage = () => setCurrentPage(totalPages);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!canManageProducts) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-muted-foreground">
        <p className="text-lg font-medium">Sin acceso</p>
        <p className="text-sm">No tenés permiso para acceder a Productos y Servicios.</p>
        <button onClick={() => navigate(-1)} className="mt-2 text-sm underline">Volver</button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Dialog open={Boolean(previewImageUrl)} onOpenChange={(open) => !open && setPreviewImageUrl(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Foto del producto</DialogTitle>
          </DialogHeader>
          {previewImageUrl && (
            <div className="flex items-center justify-center">
              <img
                src={previewImageUrl}
                alt="Foto del producto"
                className="max-h-[70vh] w-auto max-w-full rounded-lg object-contain"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
      <StockPdfDialog open={showStockPdf} onOpenChange={setShowStockPdf} />
      <Dialog open={showMarkupModal} onOpenChange={setShowMarkupModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Editar Markup Masivo</DialogTitle>
            <DialogDescription>
              Define tipo y categoría, más filtros, para actualizar markup por lote.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select
                value={markupForm.itemType}
                onValueChange={(value) => setMarkupForm((prev) => ({ ...prev, itemType: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="product">Producto</SelectItem>
                  <SelectItem value="service">Servicio</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{'Categoría'}</Label>
              <Select
                value={markupForm.category}
                onValueChange={(value) => setMarkupForm((prev) => ({ ...prev, category: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder={'Todas las categorías'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {categoryOptions.map((category) => (
                    <SelectItem key={category} value={category}>
                      {getCategoryLabel(category)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Markup (%) nuevo</Label>
              <Input
                type="number"
                min="0"
                step="0.1"
                value={markupForm.markupPercent}
                onChange={(e) => setMarkupForm((prev) => ({ ...prev, markupPercent: e.target.value }))}
                placeholder="Ej: 30"
              />
            </div>
            <div className="space-y-2">
              <Label>Marca (opcional)</Label>
              <Input
                value={markupForm.brand}
                onChange={(e) => setMarkupForm((prev) => ({ ...prev, brand: e.target.value }))}
                placeholder="Ej: Wanli"
              />
            </div>
            <div className="space-y-2">
              <Label>Modelo (opcional)</Label>
              <Input
                value={markupForm.model}
                onChange={(e) => setMarkupForm((prev) => ({ ...prev, model: e.target.value }))}
                placeholder="Ej: SP026"
              />
            </div>
            <div className="space-y-2">
              <Label>Medida (opcional)</Label>
              <Input
                value={markupForm.measure}
                onChange={(e) => setMarkupForm((prev) => ({ ...prev, measure: e.target.value }))}
                placeholder="Ej: 175/65R14"
              />
            </div>
            <div className="space-y-2">
              <Label>Proveedor (opcional)</Label>
              <Input
                value={markupForm.supplier}
                onChange={(e) => setMarkupForm((prev) => ({ ...prev, supplier: e.target.value }))}
                placeholder="Ej: San Juan y Solis"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Texto contiene (opcional)</Label>
              <Input
                value={markupForm.contains}
                onChange={(e) => setMarkupForm((prev) => ({ ...prev, contains: e.target.value }))}
                placeholder="Busca en nombre y descripcion"
              />
            </div>
          </div>
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div className="flex items-center gap-2">
              <Checkbox
                checked={markupForm.onlyVisible}
                onCheckedChange={(checked) =>
                  setMarkupForm((prev) => ({ ...prev, onlyVisible: Boolean(checked) }))
                }
              />
              <span className="text-sm">Aplicar solo sobre resultados filtrados visibles</span>
            </div>
            <Badge variant="secondary">{matchedMarkupProducts.length} coinciden</Badge>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowMarkupModal(false)} disabled={applyingMarkup}>
              Cancelar
            </Button>
            <Button onClick={handleApplyBulkMarkup} disabled={applyingMarkup}>
              {applyingMarkup ? 'Aplicando...' : 'Aplicar Markup'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <h2 className="text-2xl font-bold tracking-tight">Productos y Servicios</h2>
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
          <DropdownMenu>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className="h-9 w-9 p-0"
                    >
                      <Cog6ToothIcon className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent>Configuracion</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => navigate('/products/markup')}>
                Editar Markup
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/products/cost-adjustments')}>
                Ofertas
              </DropdownMenuItem>
              {canManageLocations && (
                <DropdownMenuItem onClick={() => setShowLocationsModal(true)}>
                  Ubicaciones
                </DropdownMenuItem>
              )}
              {canManageSettings && (
                <DropdownMenuItem onClick={() => setShowCreateCategoryModal(true)}>
                  Crear categoria
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={() => setShowStockPdf(true)}>Generar PDF Stock</Button>
          <Button
            onClick={() => setShowUploadModal(true)}
            variant="outline"
            className="hidden md:inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-card px-4 text-sm font-medium shadow-sm hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50"
          >
            <DocumentArrowDownIcon className="h-4 w-4" />
            Importar
          </Button>
          <Button
            onClick={() => { setSelectedProduct(null); setStockAdjustType('set'); setShowStockModal(true); }}
            variant="outline"
            className="h-9 items-center justify-center gap-2 rounded-lg bg-card px-4 text-sm font-medium shadow-sm hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50"
          >
            <AdjustmentsHorizontalIcon className="h-4 w-4" />
            Ajustar stock
          </Button>
          <Button
            onClick={() => navigate('/products/new')}
            className="inline-flex items-center justify-center whitespace-nowrap text-sm font-bold h-8 rounded-lg gap-1.5 px-3 bg-black dark:bg-white text-white dark:text-black shadow-xs hover:bg-black/90 dark:hover:bg-white/90"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Nuevo producto
            <kbd className="ml-2 inline-flex items-center justify-center px-1 text-xs font-bold font-sans bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-700 rounded-sm border leading-none">
              N
            </kbd>
          </Button>
        </div>
      </div>

      {/* Buscador y filtros */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
        <div className="relative md:col-span-2">
          <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground pointer-events-none z-10" />
          <Input
            type="text"
            placeholder="Buscar por nombre, SKU o categoria..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input flex min-w-0 rounded-lg border bg-white px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive h-8 w-full pl-8"
          />
        </div>
        <div className="w-full">
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="h-8 rounded-lg">
              <SelectValue placeholder="Ordenar por" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="name">Nombre</SelectItem>
              {showFinancials && (
                <>
              <SelectItem value="marginPercent">Markup</SelectItem>
                </>
              )}
              <SelectItem value="price">Precio</SelectItem>
              <SelectItem value="stock">Stock</SelectItem>
              <SelectItem value="specsDate">Especificaciones (más viejas)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-full">
          <Select
            value={filters.category || 'all'}
            onValueChange={(value) => {
              setFilters({ ...filters, category: value === 'all' ? '' : value });
              setTagsElegidas([]);   // las normas del rubro anterior no aplican
              setTagQuery('');
            }}
          >
            <SelectTrigger className="h-8 rounded-lg">
              <SelectValue placeholder="Categoria" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las categorias</SelectItem>
              {categories.map((category) => (
                <SelectItem key={category} value={category}>
                  {getCategoryLabel(category)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Filtro por etiquetas: las normas que cumple el producto. Solo
            aparece si lo que estás viendo tiene alguna. */}
        <div className="w-full">
          {!filters.category ? (
            <Tip label="Elegí primero una categoría: las normas dependen del rubro">
              <Button
                variant="outline"
                disabled
                className="h-8 w-full justify-between rounded-lg px-3 font-normal opacity-60"
              >
                <span className="truncate">Etiquetas</span>
                <ChevronUpDownIcon className="ml-1 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </Tip>
          ) : (
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="h-8 w-full justify-between rounded-lg px-3 font-normal">
                  <span className="truncate">
                    {tagsElegidas.length
                      ? `${tagsElegidas.length} ${tagsElegidas.length === 1 ? 'etiqueta' : 'etiquetas'}`
                      : 'Etiquetas'}
                  </span>
                  <ChevronUpDownIcon className="ml-1 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-0" align="start">
                <div className="border-b p-2">
                  <Input
                    value={tagQuery}
                    onChange={(e) => setTagQuery(e.target.value)}
                    placeholder="Buscar norma..."
                    className="h-8"
                    autoComplete="off"
                  />
                </div>
                <div className="max-h-64 overflow-y-auto p-1">
                  {etiquetasDisponibles
                    .filter((e) => !tagQuery.trim() || e.tag.toLowerCase().includes(tagQuery.trim().toLowerCase()))
                    .map(({ tag, n }) => (
                      <label
                        key={tag}
                        className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent"
                      >
                        <input
                          type="checkbox"
                          checked={tagsElegidas.includes(tag)}
                          onChange={() => alternarTag(tag)}
                          className="accent-primary h-4 w-4"
                        />
                        <span className="min-w-0 flex-1 truncate">{tag}</span>
                        <span className="shrink-0 text-xs text-muted-foreground">{n}</span>
                      </label>
                    ))}
                  {etiquetasDisponibles.length === 0 && (
                    <p className="px-2 py-3 text-center text-xs text-muted-foreground">
                      {'Los productos de esta categoría no tienen normas cargadas'}
                    </p>
                  )}
                  {etiquetasDisponibles.length > 0 &&
                    etiquetasDisponibles.every((e) => tagQuery.trim() && !e.tag.toLowerCase().includes(tagQuery.trim().toLowerCase())) && (
                      <p className="px-2 py-3 text-center text-xs text-muted-foreground">Ninguna norma con ese texto</p>
                    )}
                </div>
                {tagsElegidas.length > 0 && (
                  <div className="border-t p-2">
                    <Button variant="ghost" size="sm" className="h-7 w-full text-xs" onClick={() => setTagsElegidas([])}>
                      Quitar todas
                    </Button>
                  </div>
                )}
              </PopoverContent>
            </Popover>
          )}
        </div>
        <div className="w-full">
          <Select value={filters.brand || 'all'} onValueChange={(value) => setFilters({ ...filters, brand: value === 'all' ? '' : value })}>
            <SelectTrigger className="h-8 rounded-lg">
              <SelectValue placeholder="Marca" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las marcas</SelectItem>
              {marcasDisponibles.map((marca) => (
                <SelectItem key={marca} value={marca}>{marca}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-full">
          <Select value={filters.status || 'all'} onValueChange={(value) => setFilters({ ...filters, status: value === 'all' ? '' : value })}>
            <SelectTrigger className="h-8 rounded-lg">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              <SelectItem value="active">Activo</SelectItem>
              <SelectItem value="paused">Pausado</SelectItem>
              <SelectItem value="discontinued">Discontinuado</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-full">
          <Select value={(filters as any).specs || 'all'} onValueChange={(value) => setFilters({ ...filters, specs: value === 'all' ? '' : value } as any)}>
            <SelectTrigger className="h-8 rounded-lg">
              <SelectValue placeholder="Especificaciones" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Con y sin especif.</SelectItem>
              <SelectItem value="con">Especif. revisadas (con fecha)</SelectItem>
              <SelectItem value="sin">Especif. sin revisar</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Etiquetas activas: a la vista y se sacan de a una */}
      {tagsElegidas.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">Cumple con:</span>
          {tagsElegidas.map((tag) => (
            <button
              key={tag}
              type="button"
              onClick={() => alternarTag(tag)}
              className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary hover:bg-primary/20"
            >
              {tag}
              <XMarkIcon className="h-3 w-3" />
            </button>
          ))}
        </div>
      )}

      {selectedVisibleCount > 0 && (
        <div className="flex items-center justify-between rounded-lg border border-border/60 bg-muted/30 px-3 py-2">
          <span className="text-sm text-muted-foreground">
            {selectedVisibleCount} item(s) seleccionado(s)
          </span>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              className="h-8 rounded-lg"
              onClick={() => setSelectedIds(new Set())}
              disabled={bulkDeleting}
            >
              Limpiar seleccion
            </Button>
            <Button
              type="button"
              variant="destructive"
              className="h-8 rounded-lg"
              onClick={handleDeleteSelected}
              disabled={bulkDeleting}
            >
              {bulkDeleting ? 'Eliminando...' : 'Eliminar seleccionados'}
            </Button>
          </div>
        </div>
      )}

      {showRestockSuggestions && restockSuggestions.length > 0 && (
        <Card className="border border-amber-200 bg-amber-50/40">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-sm">Sugerencias de compra automatica</h3>
              <Badge variant="secondary">{restockSuggestions.length} items</Badge>
            </div>
            <div className="space-y-2">
              {restockSuggestions.slice(0, 5).map((s) => (
                <div key={s.productId} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-white px-3 py-2 text-sm">
                  <div className="min-w-0">
                    <p className="font-medium truncate">{s.name}</p>
                    <p className="text-muted-foreground">
                      {s.reason || `Stock ${s.stock} / Min ${s.minStock} - Uso ${s.recentUsageWindowDays || 90}d: ${s.recentUsage}`}
                    </p>
                  </div>
                  <Badge className="bg-gray-900 text-white">Sugerido: {s.suggestedOrderQty}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Lista de productos */}      {/* Lista de productos */}
      <Card className="border-0 shadow-none md:border md:shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600"></div>
            </div>
          ) : totalResults === 0 ? (
            <div className="text-center py-12">
              <CubeIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No hay productos ni servicios</p>
            </div>
          ) : (
            <>
              <div className="hidden md:block relative w-full overflow-x-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-muted/50 border-b">
                      <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">
                        <Checkbox
                          checked={selectAllRows}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              const next = new Set(selectedIds);
                              visibleIds.forEach((id) => next.add(id));
                              setSelectedIds(next);
                            } else {
                              const next = new Set(selectedIds);
                              visibleIds.forEach((id) => next.delete(id));
                              setSelectedIds(next);
                            }
                          }}
                        />
                      </TableHead>
                      <ColumnaOrdenable campo="name" label="Producto/Servicio" sortBy={sortBy} sortDir={sortDir} onSort={ordenarPor} />
                      <ColumnaOrdenable campo="price" label="Precio" sortBy={sortBy} sortDir={sortDir} onSort={ordenarPor} />
                      {showFinancials && (
                        <>
                          <ColumnaOrdenable campo="cost" label="Costo" sortBy={sortBy} sortDir={sortDir} onSort={ordenarPor} />
                          <ColumnaOrdenable campo="marginPercent" label="Markup" sortBy={sortBy} sortDir={sortDir} onSort={ordenarPor} />
                        </>
                      )}
                      <ColumnaOrdenable campo="stock" label="Stock" sortBy={sortBy} sortDir={sortDir} onSort={ordenarPor} align="center" />
                      <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Reposición</TableHead>
                      <TableHead className="text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedProducts.map((product) => (
                      <TableRow key={product.id} className="border-b hover:bg-muted/50 cursor-pointer">
                        <TableCell className="p-2 align-middle whitespace-nowrap text-left">
                          <Checkbox
                            checked={selectedIds.has(product.id)}
                            onCheckedChange={(checked) => {
                              const set = new Set(selectedIds);
                              if (checked) set.add(product.id);
                              else set.delete(product.id);
                              setSelectedIds(set);
                            }}
                          />
                        </TableCell>
                        <TableCell className="p-2 align-middle whitespace-nowrap text-left">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="min-w-0">
                              <button
                                type="button"
                                onClick={() => handlePreviewImage(product.imageUrl)}
                                className="font-medium text-left hover:underline"
                              >
                                {product.name}
                              </button>
                              <div className="mt-1 flex flex-wrap items-center gap-1">
                                {product.isBundle && (
                                  <Badge variant="secondary" className="bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300">
                                    Bundle
                                  </Badge>
                                )}
                                {((product as any).tags || []).length > 0 && (product as any).specsUpdatedAt && (
                                  <span className="text-[11px] text-muted-foreground">
                                    Especif. {String((product as any).specsUpdatedAt).slice(0, 10).split('-').reverse().join('/')}
                                  </span>
                                )}
                                {product.variantLabel && (
                                  <Badge variant="secondary" className="bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300">
                                    {product.variantLabel}
                                  </Badge>
                                )}
                                {product.status && product.status !== 'active' && (
                                  <Badge variant="secondary" className="bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300">
                                    {product.status === 'paused' ? 'Pausado' : 'Discontinuado'}
                                  </Badge>
                                )}
                              </div>
                              {product.description && (
                                <div className="text-sm text-muted-foreground truncate max-w-xs">
                                  {product.description}
                                </div>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="p-2 align-middle whitespace-nowrap text-left">
                          <div className="font-medium">${product.sellingPrice?.toLocaleString()}</div>
                        </TableCell>
                        {showFinancials && (
                          <>
                            <TableCell className="p-2 align-middle whitespace-nowrap text-left">
                              <div>{product.category === 'servicio' ? '-' : formatMoney(product.costPrice)}</div>
                            </TableCell>
                            <TableCell className="p-2 align-middle whitespace-nowrap text-left">
                              <div>{product.category === 'servicio' || !product.costPrice ? '-' : `${(product.profitMargin ?? ((product.sellingPrice - product.costPrice) / product.costPrice * 100)).toFixed(1)}%`}</div>
                            </TableCell>
                          </>
                        )}
                        <TableCell className="p-2 align-middle whitespace-nowrap text-center">
                          {product.category !== 'servicio' ? (
                            <div>{product.currentStock}</div>
                          ) : (
                            <div>-</div>
                          )}
                        </TableCell>
                        <TableCell className="p-2 align-middle whitespace-nowrap text-left">
                          {product.category !== 'servicio' ? (
                            <Badge
                              variant="secondary"
                              className={getStockStatusColor(product)}
                            >
                              {getStockStatusText(product)}
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                              Servicio
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="p-2 align-middle whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end">
                            <ButtonGroup>
                              <Tip label="Ver ficha">
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/products/${product.id}/view`, { state: { product } });
                                }}
                                className="size-8"
                              >
                                <EyeIcon className="h-4 w-4" />
                              </Button>
                              </Tip>
                              <Tip label="Editar">
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/products/${product.id}/edit`, { state: { product } });
                                }}
                                className="size-8"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </Button>
                              </Tip>
                              {product.category !== 'servicio' && (
                                <Tip label="Actualizar stock">
                                <Button
                                  variant="outline"
                                  size="icon"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedProduct(product);
                                    setShowStockModal(true);
                                  }}
                                  className="size-8"
                                >
                                  <ShoppingCartIcon className="h-4 w-4" />
                                </Button>
                                </Tip>
                              )}
                              <Tip label="Eliminar">
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(product.id);
                                }}
                                className="size-8"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </Button>
                              </Tip>
                            </ButtonGroup>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="md:hidden space-y-3 p-4">
                {paginatedProducts.map((product) => (
                <div key={product.id} className="rounded-xl border bg-card/80 p-4 shadow-sm space-y-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex min-w-0 items-start gap-3">
                          <Checkbox
                            checked={selectedIds.has(product.id)}
                            onCheckedChange={(checked) => {
                            const set = new Set(selectedIds);
                            if (checked) set.add(product.id);
                          else set.delete(product.id);
                          setSelectedIds(set);
                        }}
                        />
                        <div className="min-w-0">
                          <p className="text-xs text-muted-foreground">Producto/Servicio</p>
                          <button
                            type="button"
                            onClick={() => handlePreviewImage(product.imageUrl)}
                            className="text-base font-semibold text-left hover:underline break-words"
                          >
                            {product.name}
                          </button>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {product.isBundle && <Badge variant="secondary">Bundle</Badge>}
                            {product.variantLabel && <Badge variant="secondary">{product.variantLabel}</Badge>}
                            {product.status && product.status !== 'active' && (
                              <Badge variant="secondary">{product.status === 'paused' ? 'Pausado' : 'Discontinuado'}</Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground font-mono break-all">{product.sku}</p>
                        </div>
                      </div>
                      {product.category !== 'servicio' ? (
                        <Badge variant="secondary" className={getStockStatusColor(product)}>
                          {getStockStatusText(product)}
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                          Servicio
                        </Badge>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-xs uppercase text-muted-foreground">Categoría</p>
                        <p className="font-medium">{getCategoryLabel(product.category)}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-muted-foreground">Precio</p>
                        <p className="font-semibold">${product.sellingPrice?.toLocaleString()}</p>
                      </div>
                      {product.category !== 'servicio' && (
                        <div>
                          <p className="text-xs uppercase text-muted-foreground">Stock</p>
                          <p>
                            {product.unit
                              ? `${product.currentStock} ${getUnitLabel(product.unit)}`
                              : product.currentStock}
                          </p>
                        </div>
                      )}
                      {product.description && (
                        <div className="col-span-2">
                          <p className="text-muted-foreground">{product.description}</p>
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full justify-center"
                        onClick={() => navigate(`/products/${product.id}/view`, { state: { product } })}
                      >
                        <EyeIcon className="h-4 w-4 mr-1" />
                        Ficha
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full justify-center"
                        onClick={() => navigate(`/products/${product.id}/edit`, { state: { product } })}
                      >
                        <PencilIcon className="h-4 w-4 mr-1" />
                        Editar
                      </Button>
                      {product.category !== 'servicio' && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full justify-center"
                          onClick={() => {
                            setSelectedProduct(product);
                            setShowStockModal(true);
                          }}
                        >
                          <ShoppingCartIcon className="h-4 w-4 mr-1" />
                          Stock
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="col-span-2 w-full justify-center text-destructive"
                        onClick={() => handleDelete(product.id)}
                      >
                        <TrashIcon className="h-4 w-4 mr-1" />
                        Eliminar
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {!loading && totalResults > 0 && (
        <div className="flex items-center justify-between px-2">
          <div className="hidden flex-1 text-muted-foreground text-sm md:block">
            Mostrando {startResult} a {endResult} de {totalResults} resultados
          </div>
          <div className="flex items-center space-x-6 lg:space-x-8">
            <div className="flex w-[100px] items-center justify-center font-medium text-sm">
              Página {currentPage} de {totalPages}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="icon"
              onClick={goToFirstPage}
              disabled={currentPage === 1}
              className="shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium text-sm outline-none transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0 border bg-card shadow-xs hover:bg-accent hover:text-accent-foreground active:scale-[0.97] dark:border-input dark:bg-input/30 dark:hover:bg-input/50 size-9 hidden h-8 w-8 lg:flex"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                <path d="m11 17-5-5 5-5"></path>
                <path d="m18 17-5-5 5-5"></path>
              </svg>
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
              className="inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium text-sm outline-none transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0 border bg-card shadow-xs hover:bg-accent hover:text-accent-foreground active:scale-[0.97] dark:border-input dark:bg-input/30 dark:hover:bg-input/50 size-9 h-8 w-8"
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              className="inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium text-sm outline-none transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0 border bg-card shadow-xs hover:bg-accent hover:text-accent-foreground active:scale-[0.97] dark:border-input dark:bg-input/30 dark:hover:bg-input/50 size-9 h-8 w-8"
            >
              <ChevronRightIcon className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={goToLastPage}
              disabled={currentPage === totalPages}
              className="shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium text-sm outline-none transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0 border bg-card shadow-xs hover:bg-accent hover:text-accent-foreground active:scale-[0.97] dark:border-input dark:bg-input/30 dark:hover:bg-input/50 size-9 hidden h-8 w-8 lg:flex"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                <path d="m6 17 5-5-5-5"></path>
                <path d="m13 17 5-5-5-5"></path>
              </svg>
            </Button>
          </div>
        </div>
      )}

      {false && <div />}

      {/* Modal de ubicaciones de productos */}
      <Dialog open={showLocationsModal} onOpenChange={setShowLocationsModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Ubicaciones de productos</DialogTitle>
            <DialogDescription>
              Administra las opciones del selector de ubicacion.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <Input
                value={locationName}
                onChange={(e) => setLocationName(e.target.value)}
                placeholder="Nueva ubicacion"
              />
              <Button type="button" onClick={handleSaveLocation}>
                {editingLocationId ? "Actualizar" : "Agregar"}
              </Button>
              {editingLocationId && (
                <Button type="button" variant="outline" onClick={resetLocationForm}>
                  Cancelar
                </Button>
              )}
            </div>
            <div className="space-y-2 max-h-[260px] overflow-y-auto rounded-lg border p-3">
              {loadingLocations ? (
                <div className="text-sm text-muted-foreground">Cargando...</div>
              ) : locations.length ? (
                locations.map((loc) => (
                  <div
                    key={loc.id}
                    className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2"
                  >
                    <span className="text-sm">{loc.name}</span>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => handleEditLocation(loc)}
                        className="h-8 w-8"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => handleDeleteLocation(loc.id)}
                        className="h-8 w-8"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-sm text-muted-foreground">
                  No hay ubicaciones cargadas.
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showCreateCategoryModal}
        onOpenChange={(open) => {
          setShowCreateCategoryModal(open);
          if (!open) {
            setNewCategoryName('');
            setNewCategoryDescription('');
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Crear categoria</DialogTitle>
            <DialogDescription>
              Crea una categoria nueva para usar en productos y servicios.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre (clave)</Label>
              <Input
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="Ej: amortiguadores"
              />
              <p className="text-xs text-muted-foreground">
                Se guarda normalizado en minusculas (sin tildes y con guion bajo).
              </p>
            </div>
            <div className="space-y-2">
              <Label>Descripcion (opcional)</Label>
              <Input
                value={newCategoryDescription}
                onChange={(e) => setNewCategoryDescription(e.target.value)}
                placeholder="Descripcion corta"
              />
            </div>
            <div className="space-y-2">
              <Label>Categorias existentes</Label>
              <div className="max-h-40 overflow-y-auto rounded-lg border p-2">
                {catalogCategories.length ? (
                  <div className="space-y-1">
                    {catalogCategories.map((cat) => (
                      <div key={cat.id || cat.name} className="rounded-md border px-2 py-1 text-sm">
                        <div className="font-medium">{cat.name}</div>
                        {cat.description ? (
                          <div className="text-xs text-muted-foreground">{cat.description}</div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground">No hay categorias cargadas.</div>
                )}
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowCreateCategoryModal(false)} disabled={savingCategory}>
              Cancelar
            </Button>
            <Button onClick={handleCreateCategory} disabled={savingCategory}>
              {savingCategory ? 'Guardando...' : 'Crear categoria'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de actualizaci??n de stock */}
      <Dialog open={showStockModal} onOpenChange={(open) => {
        if (!open) { setSelectedProduct(null); setStockAdjustQty(''); setStockAdjustReason(''); setStockAdjustType('add'); }
        setShowStockModal(open);
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Ajuste de stock</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedProduct ? (
              <div className="rounded-md border px-3 py-2 bg-muted text-sm">
                <span className="font-medium">{selectedProduct.name}</span>
                <span className="ml-2 text-muted-foreground">· Stock actual: {selectedProduct.currentStock ?? 0}</span>
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label>Producto</Label>
                <ProductCombobox
                  products={products.filter((p: any) => p.category !== 'servicio')}
                  selectedProduct={null}
                  onSelect={(p: any) => setSelectedProduct(p)}
                  placeholder="Buscar producto..."
                />
              </div>
            )}

            <div className="space-y-1.5">
              <Label>Tipo de ajuste</Label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => setStockAdjustType('set')}
                  className={`rounded-md border py-2 text-sm font-medium transition-colors ${stockAdjustType === 'set' ? 'border-blue-500 bg-blue-500/10 text-blue-500' : 'border-border hover:bg-accent'}`}
                >
                  = Stock real
                </button>
                <button
                  type="button"
                  onClick={() => setStockAdjustType('add')}
                  className={`rounded-md border py-2 text-sm font-medium transition-colors ${stockAdjustType === 'add' ? 'border-green-500 bg-green-500/10 text-green-600' : 'border-border hover:bg-accent'}`}
                >
                  + Entrada
                </button>
                <button
                  type="button"
                  onClick={() => setStockAdjustType('subtract')}
                  className={`rounded-md border py-2 text-sm font-medium transition-colors ${stockAdjustType === 'subtract' ? 'border-red-500 bg-red-500/10 text-red-500' : 'border-border hover:bg-accent'}`}
                >
                  − Salida
                </button>
              </div>
              {stockAdjustType === 'set' && (
                <p className="text-xs text-muted-foreground">Ponés lo que contaste y el sistema ajusta la diferencia solo.</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label>{stockAdjustType === 'set' ? 'Stock real contado' : 'Cantidad'}</Label>
              <Input
                type="number"
                min={stockAdjustType === 'set' ? 0 : 1}
                value={stockAdjustQty}
                onChange={(e) => setStockAdjustQty(e.target.value)}
                placeholder={stockAdjustType === 'set' ? 'Ej: 12' : 'Ej: 5'}
                autoFocus
              />
              {stockAdjustQty !== '' && !Number.isNaN(parseInt(stockAdjustQty)) && selectedProduct && (
                <p className="text-xs text-muted-foreground">
                  {stockAdjustType === 'set'
                    ? (() => {
                        const delta = parseInt(stockAdjustQty) - Number(selectedProduct.currentStock ?? 0);
                        return delta === 0 ? 'Sin cambios' : `Diferencia: ${delta > 0 ? '+' : ''}${delta} unidades`;
                      })()
                    : `Stock resultante: ${stockAdjustType === 'add'
                        ? Number(selectedProduct.currentStock ?? 0) + parseInt(stockAdjustQty)
                        : Number(selectedProduct.currentStock ?? 0) - parseInt(stockAdjustQty)}`}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label>Motivo (opcional)</Label>
              <Input
                value={stockAdjustReason}
                onChange={(e) => setStockAdjustReason(e.target.value)}
                placeholder="Ej: Corrección de inventario, merma, devolución..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStockModal(false)}>Cancelar</Button>
            <Button
              onClick={onStockAdjust}
              disabled={stockAdjustSaving || !selectedProduct || stockAdjustQty === '' || (stockAdjustType === 'set' ? parseInt(stockAdjustQty) < 0 : parseInt(stockAdjustQty) <= 0)}
              className={stockAdjustType === 'add' ? 'bg-green-600 hover:bg-green-700 text-white' : stockAdjustType === 'subtract' ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}
            >
              {stockAdjustSaving ? 'Guardando...' : stockAdjustType === 'set' ? 'Ajustar al stock real' : stockAdjustType === 'add' ? 'Registrar entrada' : 'Registrar salida'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de subida de CSV */}
      <Dialog
        open={showUploadModal}
        onOpenChange={(open) => {
          setShowUploadModal(open);
          if (!open) {
            setUploadFile(null);
            setCsvPreviewRows(null);
          }
        }}
      >
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Importar lista de proveedor</DialogTitle>
            <DialogDescription>
              {uploadConfig.importType === 'neumaticos'
                ? 'Excel o CSV con columnas: Código, Modelo, Medida, Precio Lista, Costo.'
                : 'Excel o CSV con columnas: Código, Nombre, Costo. Opcionales: Marca, Modelo, Etiquetas, Viscosidad, Capacidad, Stock.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{'Categor\u00eda de esta lista'}</Label>
              <Select
                value={uploadConfig.defaultCategory}
                onValueChange={(value) => setUploadConfig((prev) => ({
                  ...prev,
                  defaultCategory: value,
                  // Los neum\u00e1ticos arman el nombre distinto (modelo + medida) y
                  // leen medida/\u00edndices: el resto va por el camino com\u00fan.
                  importType: value === 'neumaticos' ? 'neumaticos' as const : 'productos' as const
                }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIAS_IMPORT.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {'Es la categor\u00eda que se le pone a las filas que no traigan una propia en la columna "categoria".'}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="csv-file">Archivo CSV o Excel</Label>
              <Input id="csv-file" type="file" accept=".csv,.xlsx,.xls" onChange={handleCsvFileChange} />
              {uploadFile && (
                <p className="text-xs text-muted-foreground">
                  Archivo seleccionado: <span className="font-medium">{uploadFile.name}</span>
                </p>
              )}
              {csvPreviewRows !== null && (
                <p className="text-xs text-muted-foreground">
                  Registros detectados para importar: <span className="font-medium">{csvPreviewRows}</span>
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Proveedor de esta lista</Label>
              {/* Combobox: con 23 proveedores y creciendo, buscarlo tipeando
                  es más rápido que scrollear la lista */}
              <SearchableSelect
                value={uploadConfig.supplierName || '__none__'}
                onChange={(value) =>
                  setUploadConfig((prev) => ({ ...prev, supplierName: value === '__none__' ? '' : value }))
                }
                placeholder={loadingImportProviders ? 'Cargando proveedores...' : 'Escribí para buscar el proveedor'}
                emptyText="No hay ningún proveedor con ese nombre"
                options={[
                  { value: '__none__', label: 'Sin proveedor' },
                  // Se muestra por el nombre con el que se lo conoce; la razón
                  // social queda de alias para poder buscarlo igual.
                  ...importProviders
                    .map((provider) => {
                      const visible = provider.fantasyName || provider.name;
                      return {
                        orden: visible,
                        value: provider.name,
                        label: provider.esCliente ? `${visible} · también cliente` : visible,
                        alias: provider.name
                      };
                    })
                    .sort((a, b) => a.orden.localeCompare(b.orden, 'es', { sensitivity: 'base' }))
                ]}
              />
            </div>

            <div className="space-y-2">
              <Label>Estrategia de precio base</Label>
              <Select
                value={uploadConfig.pricingStrategy}
                onValueChange={(value) => setUploadConfig((prev) => ({ ...prev, pricingStrategy: value }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cost_plus_margin">Costo + % utilidad</SelectItem>
                  <SelectItem value="excel_list">Usar Precio Lista</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {uploadConfig.pricingStrategy === 'cost_plus_margin' && (
              <div className="space-y-2">
                <Label htmlFor="utility-percent">Utilidad (%)</Label>
                <Input
                  id="utility-percent"
                  type="number"
                  min="0"
                  step="0.01"
                  value={uploadConfig.utilityPercent}
                  onChange={(e) => setUploadConfig((prev) => ({ ...prev, utilityPercent: e.target.value }))}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>{'Cuando el c\u00f3digo ya existe'}</Label>
              <Select
                value={uploadConfig.duplicateMode}
                onValueChange={(value) => setUploadConfig((prev) => ({ ...prev, duplicateMode: value }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="update">Actualizar existente</SelectItem>
                  <SelectItem value="skip">Omitir fila</SelectItem>
                  <SelectItem value="error">Marcar error</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-between items-center">
              <Button type="button" onClick={() => downloadTemplate()} variant="outline" size="sm">
                <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                Descargar Plantilla
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadFile(null);
                  setCsvPreviewRows(null);
                  setPreview(null);
                  setDecisiones({});
                }}
              >
                Cancelar
              </Button>
              {preview ? (
                <Button type="button" onClick={submitCsvImport} disabled={uploadingCsv}>
                  {uploadingCsv ? 'Importando...' : 'Confirmar e importar'}
                </Button>
              ) : (
                <Button type="button" onClick={revisarAntesDeImportar} disabled={!uploadFile || revisando}>
                  {revisando ? 'Revisando...' : 'Revisar'}
                </Button>
              )}
            </div>

            {/* Paso de revisión: qué va a pasar antes de escribir nada */}
            {preview && (
              <div className="space-y-3 rounded-lg border border-border bg-muted/30 p-3">
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                  <span><strong>{preview.resumen?.total || 0}</strong> filas</span>
                  <span className="text-emerald-600 dark:text-emerald-400"><strong>{preview.resumen?.crear || 0}</strong> nuevos</span>
                  <span className="text-blue-600 dark:text-blue-400"><strong>{preview.resumen?.actualizar || 0}</strong> se actualizan</span>
                  {(preview.resumen?.dudosos || 0) > 0 && (
                    <span className="text-amber-600 dark:text-amber-400"><strong>{preview.resumen.dudosos}</strong> dudosos</span>
                  )}
                  {(preview.resumen?.errores || 0) > 0 && (
                    <span className="text-destructive"><strong>{preview.resumen.errores}</strong> con error</span>
                  )}
                </div>

                {(preview.dudosos || []).length > 0 ? (
                  <>
                    <p className="text-xs text-muted-foreground">
                      {'Estas filas crearían un producto nuevo, pero se parecen a uno que ya tenés. Marcá el que sea el mismo para que lo actualice en vez de duplicarlo.'}
                    </p>
                    <ul className="space-y-2">
                      {preview.dudosos.map((d: any) => (
                        <li key={d.clave} className="rounded-md border border-border bg-background p-2 text-sm">
                          <p className="font-medium">{d.nombre}</p>
                          <div className="mt-1 space-y-1">
                            {d.candidatos.map((c: any) => (
                              <label key={c.id} className="flex cursor-pointer items-center gap-2 text-xs">
                                <input
                                  type="radio"
                                  name={`dudoso-${d.clave}`}
                                  checked={decisiones[d.clave] === c.id}
                                  onChange={() => setDecisiones((prev) => ({ ...prev, [d.clave]: c.id }))}
                                  className="accent-primary"
                                />
                                <span>
                                  Es el mismo que <strong>{c.name}</strong>
                                  <span className="text-muted-foreground"> (stock {c.stock})</span>
                                </span>
                              </label>
                            ))}
                            <label className="flex cursor-pointer items-center gap-2 text-xs">
                              <input
                                type="radio"
                                name={`dudoso-${d.clave}`}
                                checked={!decisiones[d.clave]}
                                onChange={() => setDecisiones((prev) => {
                                  const next = { ...prev };
                                  delete next[d.clave];
                                  return next;
                                })}
                                className="accent-primary"
                              />
                              <span>Es un producto nuevo, crearlo</span>
                            </label>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    {'No hay nada dudoso: ninguna fila se parece a un producto que ya tengas con otro nombre.'}
                  </p>
                )}
              </div>
            )}

            <details className="rounded-lg border bg-muted/50">
              <summary className="p-3 cursor-pointer font-medium hover:bg-muted/80">Ver formato esperado</summary>
              <div className="p-3 pt-0 border-t space-y-3 text-xs text-muted-foreground">
                {uploadConfig.importType === 'neumaticos' ? (
                  <>
                    <p><strong>Columnas:</strong> {'C\u00f3digo, Modelo, Medida, Precio Lista, Costo.'}</p>
                    <p><strong>Nombre final:</strong> se arma como Modelo + Medida.</p>
                  </>
                ) : (
                  <>
                    <p><strong>Columnas:</strong> {'C\u00f3digo, Nombre, Costo. Opcionales: Categoria, Marca, Modelo, Etiquetas, Viscosidad, Capacidad, Stock, Stockminimo, Precio Lista.'}</p>
                    <p><strong>Nombre:</strong> se usa directamente como nombre del producto.</p>
                    <p><strong>Etiquetas:</strong> {'las normas separadas por punto y coma ("API SN/CF; ACEA A3"). El sistema las abre en etiquetas sueltas y quedan buscables.'}</p>
                    <p><strong>Marca:</strong> {'sale de la primera palabra de Modelo, as\u00ed que el modelo va con la marca adelante ("Valvoline Advanced").'}</p>
                  </>
                )}
                <p><strong>SKU:</strong> {'se toma de C\u00f3digo.'}</p>
                <p><strong>Recargo tarjeta:</strong> {'se sigue aplicando al vender (no en esta importaci\u00f3n).'}</p>
              </div>
            </details>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Products;



